import React, { FC, useState, useCallback, useEffect } from "react";
import CrewScreen from "../atoms/CrewScreen";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { service } from "../../../services/AxiosServices";
import SectionHeader from "./SectionHeader";
import WindowHeightComponent from "../../Layouts/WindowHeight";
import AccessControl from "./AccessControl";
import AccessDenied from "../atoms/AccessDenied";
import Multiselect from "multiselect-react-dropdown";
import x from "../../static/images/x.png";
const CrewForm: FC = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    values: {
      crewName: "",
      description: "",
      createdBy: "",
      associated_users: [] as number[],
    }, // Holds form values
    clientErrors: {} as Record<string, string>, // Stores client-side validation errors
    serverError: null as string | null, // Stores server-side errors
  });
  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
  // Function to validate form fields
  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    if (!values.crewName.trim()) {
      errors.crewName = "Crew name field is required.";
    }
    return errors;
  };


  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await service<{ response: any }>("/user-list", "post");
        const usersData = response.data.response
          .map((user: any) => ({
            id: user.id,
            name:
              `${(user.first_name || "").trim()} ${(
                user.last_name || ""
              ).trim()}`.trim() || user.email,
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        setUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  const handleUserSelect = useCallback((selectedList: any[]) => {
    const selectedIds = selectedList.map((user) => user.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, associated_users: selectedIds },
      clientErrors: { ...prev.clientErrors, associated_users: "" },
    }));
  }, []);

  const handleUserRemove = useCallback((removedList: any[]) => {
    const remainingIds = removedList.map((user) => user.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, associated_users: remainingIds },
      clientErrors: { ...prev.clientErrors, associated_users: "" },
    }));
  }, []);

  // Handles input field changes and updates state
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        clientErrors: { ...prev.clientErrors, [name]: "" },
        serverError: null,
      }));
    },
    []
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const errors = validations(formState.values);
      if (Object.keys(errors).length === 0) {
        try {
          const response = await service<{ response: { id: string } }>(
            "api/create-crew",
            "post",
            {
              project_id: localStorage.getItem("project_id"),
              crew_name: formState.values.crewName,
              crew_description: formState.values.description,
              created_updated_by: localStorage.getItem("user"),
              associated_users: formState.values.associated_users.join(","),
            }
          );

          const id = response?.data?.response?.id;
          toast.success("Crew created successfully!", { autoClose: 2000 });
          navigate(`/edit_crew/${id}`, {
            state: { crewName: formState.values.crewName },
          });
        } catch (err: any) {
          const errorMessage =
            err.response?.data?.error || "Something went wrong.";
          setFormState((prev) => ({ ...prev, serverError: errorMessage }));
        }
      } else {
        setFormState((prev) => ({ ...prev, clientErrors: errors }));
      }
    },
    [formState.values, navigate]
  );
  // Convert client-side errors object to an array of messages
  const clientErrorsArray = Object.entries(formState.clientErrors).reduce(
    (acc: string[], [field, error]) => {
      if (error) {
        acc.push(`${error}`);
      }
      return acc;
    },
    []
  );

  return (
    <>
      <AccessControl
        requiredPermissions={[{ permission: "create_crew", types: ["create"] }]}
        renderNoAccess={<AccessDenied/>}
      >
        <SectionHeader back={true} title="Create crew"></SectionHeader>
        <WindowHeightComponent className={""}>
          <div className="p-3">
            {formState.serverError && (
              <div className="alert alert-danger">{formState.serverError}</div>
            )}
            {clientErrorsArray.length > 0 && (
              <div className="alert alert-danger">
                <ul>
                  {clientErrorsArray.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="crewName" className="LabelText mb-1">
                  Crew name:<span style={{ color: "red" }}>*</span>
                </label>

                <input
                  type="text"
                  id="crewName"
                  name="crewName"
                  className="form-control"
                  value={formState.values.crewName}
                  onChange={handleChange}
                />
                <br />
                <label htmlFor="description" className="LabelText mb-1">
                  Crew description:
                </label>

                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  rows={5}
                  cols={5}
                  value={formState.values.description}
                  onChange={handleChange}
                />

                <label htmlFor="associated_users" className="LabelText mb-1">
                  Select users:
                </label>
                <Multiselect
                  options={users}
                  selectedValues={users.filter((user) =>
                    formState.values.associated_users.includes(user.id)
                  )}
                  onSelect={handleUserSelect}
                  onRemove={handleUserRemove}
                  displayValue="name"
                  id="associated_users"
                  customCloseIcon={
                    <img
                      style={{
                        width: "1rem",
                        height: "1rem",
                        marginLeft: "1rem",
                      }}
                      src={x}
                      alt="close"
                    />
                  }
                />
              </div>

              <div className="crew-screen mt-3">
                <CrewScreen showScroll={false} />
              </div>
              <div className="d-flex justify-content-end">
                <button type="submit" className="btn SaveBtn mt-3">
                  Save
                </button>
              </div>
            </form>
          </div>
        </WindowHeightComponent>
      </AccessControl>
    </>
  );
};

export default CrewForm;
